import { IDate } from '@models';
import { NotificationService, Order } from '@services';
import { useStore } from '@stores';
import { formatDateWithOffset, isTimeTbc, roundUpToNext15, Rules } from '@utils';
import { Alert, Button, Form, Input, message, Modal, Switch, Typography } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { TechnicianSelect } from '@components';
import { DateTimeSelect } from './DateTimeSelect';

const { Text } = Typography;

interface IProps {
  open: boolean;
  order: Order;
  title?: string;
  isInitial?: boolean;
  /**
   * Ask for the booking time and (optionally) a technician as well as the drop-off. This is the
   * case for a card dropped on a week or month day: the drop fixed the date but nothing else.
   * In the day view the column and the slot have already answered both, so only the drop-off
   * is asked.
   */
  schedule?: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
  onSaved?: (order: Order) => void;
}

export const DropOffDateModalForm: React.FC<IProps> = observer(
  ({ open, order, title, isInitial = false, schedule = false, setOpen, onCancel, onSaved }) => {
    const { orderStore, availabilityStore } = useStore();
    // Read straight from the store rather than through useWorkAvailabilities: that hook clears
    // the store on unmount, and this modal unmounts every time it closes - it would blank the
    // calendar's own free-time labels behind it.
    const { workAvailabilities } = availabilityStore;
    const [form] = Form.useForm();

    const [dropOff, setDropOff] = useState<IDate>();
    const [booking, setBooking] = useState<IDate>();
    const [technicianId, setTechnicianId] = useState<number>();
    // Once the drop-off has been set by hand it stops following the booking start.
    const [dropOffTouched, setDropOffTouched] = useState(false);

    const durationHours = order?.duration && order.duration > 0 ? order.duration : 1;

    useEffect(() => {
      if (schedule && workAvailabilities.size === 0) availabilityStore.getWorkAvailabilities();
    }, [schedule, workAvailabilities.size, availabilityStore]);

    // The first bookable moment of a day: the garage's opening time that weekday, or now
    // (rounded up) if that has already passed today.
    const dayStart = (day: dayjs.Dayjs) => {
      const availabilities = Array.from(workAvailabilities.values());
      const availability = availabilities.find((a) => a.weekday === day.format('dddd'));
      const [h, m] = (availability?.startTime ?? '08:00').split(':').map(Number);
      const opening = day.hour(h || 8).minute(m || 0).second(0).millisecond(0);
      const now = dayjs();
      return opening.isBefore(now) && day.isSame(now, 'day') ? roundUpToNext15(now) : opening;
    };

    useEffect(() => {
      if (!order) return;

      const now = dayjs();
      const dropOffMoment = dayjs(order.dropOffDate);

      const effectiveNow = roundUpToNext15(now);

      // The booking starts where the card landed: a drop on a day knows the day, not the hour,
      // so it starts as "TBC" (midnight, see isTimeTbc) for the user to give an hour; a card
      // moved day to day keeps its hour. The drop-off is a real time regardless.
      const bookingStart = schedule ? dayjs(order.startAt) : undefined;

      // Drop-off default, in order of preference: the job's own drop-off if it is still ahead -
      // even when the job has just been moved to another day: it stays put and the out-of-sync
      // alert below offers the move, rather than being changed silently; the garage opening for
      // a day-only booking; otherwise the slot it was just dropped on (the customer drops the
      // car off when the work starts), or failing that, now.
      const slotStart = order.startAt && !isTimeTbc(order.startAt) ? dayjs(order.startAt) : undefined;
      const startAt =
        order.dropOffDate && !dropOffMoment.isBefore(now)
          ? order.dropOffDate
          : bookingStart && isTimeTbc(order.startAt)
            ? formatDateWithOffset(dayStart(bookingStart))
            : formatDateWithOffset(slotStart && !slotStart.isBefore(now) ? slotStart : effectiveNow);

      setDropOff({ startAt });
      setDropOffTouched(false);
      setTechnicianId(order.userId ? Number(order.userId) : undefined);
      setBooking(bookingStart ? { startAt: formatDateWithOffset(bookingStart) } : undefined);

      form.setFieldsValue({
        id: order.id,
        sendPush: false,
        userId: order.userId,
        startAt: bookingStart
          ? formatDateWithOffset(bookingStart)
          : order.startAt
            ? formatDateWithOffset(order.startAt)
            : undefined,
        endAt: bookingStart
          ? formatDateWithOffset(bookingStart.add(durationHours, 'hour'))
          : order.endAt
            ? formatDateWithOffset(order.endAt)
            : undefined,
        dropOffDate: startAt,
      });
      // dayStart reads the availabilities, which may still be loading; re-running for them is the point.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, order, schedule, workAvailabilities.size]);

    const handleUpdate = async (values: any) => {
      if (dayjs(values.dropOffDate).isBefore(dayjs().format())) {
        message.error('The selected date and time cannot be in the past.');
        return;
      }
      // A TBC booking is judged by its day (midnight today is "today", not "in the past").
      const bookingTooEarly = isTimeTbc(values.startAt)
        ? dayjs(values.startAt).isBefore(dayjs(), 'day')
        : dayjs(values.startAt).isBefore(dayjs());
      if (schedule && bookingTooEarly) {
        message.error('The booking cannot start in the past.');
        return;
      }
      setOpen(false);

      const result = await orderStore.updateCalendar({
        id: values.id,
        userId: values.userId,
        // A rescheduled job whose technician was set back to "Unassigned": a null userId alone
        // would mean "unchanged" to the API.
        clearTechnician: schedule && !values.userId,
        startAt: values.startAt,
        endAt: values.endAt,
        workStationId: order.workStationId,
        dropOffDate: values.dropOffDate,
      });

      if (result) {
        // Only once the booking is saved - a push about a drop-off that failed to save would
        // tell the customer a time the garage does not have - and only when asked (the API
        // does nothing else with the call).
        if (values.sendPush) {
          try {
            await NotificationService.create({
              isInitial,
              sendPush: true,
              orderId: values.id,
              dropOffDate: values.dropOffDate,
            });
          } catch (error) {
            // Best-effort; the booking is saved either way.
            console.error(error);
          }
        }
        form.resetFields();
        onSaved?.(result);
      } else {
        // Nothing was saved (the store has shown the error): let the caller undo whatever it
        // put on screen in anticipation - on the calendar that is the dropped card.
        onCancel();
      }
    };

    const setDropOffDate = (value: IDate) => {
      setDropOffTouched(true);
      setDropOff({ startAt: formatDateWithOffset(value.startAt) });
      form.setFieldsValue({ dropOffDate: formatDateWithOffset(value.startAt) });
    };

    // The drop-off is expected on the booking's day. Picked by hand onto another day (or left on
    // the old day of a rescheduled job), it is flagged - not blocked - with the same one-click
    // sync the job card offers: same day, keeping its time, never after the job start.
    const bookingStartAt = schedule ? booking?.startAt : order?.startAt;
    const dropOffOutOfSync =
      !!bookingStartAt && !!dropOff?.startAt && !dayjs(dropOff.startAt).isSame(dayjs(bookingStartAt), 'day');

    const handleSyncDropOff = () => {
      if (!bookingStartAt) return;
      const jobStart = dayjs(bookingStartAt);
      const current = dropOff?.startAt ? dayjs(dropOff.startAt) : jobStart;
      let synced = jobStart.hour(current.hour()).minute(current.minute()).second(0);
      // A TBC job starts "sometime that day": the drop-off keeps its time rather than being
      // pulled to midnight.
      if (!isTimeTbc(bookingStartAt) && synced.isAfter(jobStart)) synced = jobStart;
      setDropOffDate({ startAt: formatDateWithOffset(synced) });
    };

    const setBookingDate = (value: IDate) => {
      const start = dayjs(value.startAt);
      const startAt = formatDateWithOffset(start);
      setBooking({ startAt });
      form.setFieldsValue({
        startAt,
        endAt: formatDateWithOffset(start.add(durationHours, 'hour')),
      });
      // The drop-off follows a real booking time; a TBC booking leaves it where it is.
      if (!dropOffTouched && !isTimeTbc(startAt)) {
        setDropOff({ startAt });
        form.setFieldsValue({ dropOffDate: startAt });
      }
    };

    const modalTitle = title || (schedule ? 'Schedule job' : 'Confirm drop-off');

    // What the booking now looks like, for the header: the slot the card landed on (or the
    // day, for a schedule drop) and who it landed on.
    const bookedOn = schedule
      ? booking?.startAt
        ? isTimeTbc(booking.startAt)
          ? `${dayjs(booking.startAt).format('ddd D MMM')} · TBC`
          : `${dayjs(booking.startAt).format('ddd D MMM, HH:mm')}–${dayjs(booking.startAt).add(durationHours, 'hour').format('HH:mm')}`
        : undefined
      : order.startAt
        ? `${dayjs(order.startAt).format('ddd D MMM, HH:mm')}${order.endAt ? `–${dayjs(order.endAt).format('HH:mm')}` : ''}`
        : undefined;

    return (
      <Modal
        width={440}
        title={modalTitle}
        transitionName=""
        maskTransitionName=""
        open={open}
        onCancel={onCancel}
        afterClose={() => form.resetFields()}
        closable={!orderStore.calendarLoading}
        maskClosable={!orderStore.calendarLoading}
        footer={
          <>
            <Button onClick={onCancel} disabled={orderStore.calendarLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={orderStore.calendarLoading}
              onClick={() => form.submit()}
            >
              {schedule ? 'Schedule' : 'Confirm'}
            </Button>
          </>
        }
      >
        {/* The job this is about - the calendar's card has just left the reader's sight. */}
        {(order.registrationNumber || order.title) && (
          <JobHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {order.registrationNumber && <Plate>{order.registrationNumber}</Plate>}
              <JobTitle>{order.title}</JobTitle>
            </div>
            <JobMeta>
              {order.customer?.fullname && <span>{order.customer.fullname}</span>}
              {bookedOn && (
                <span>
                  {bookedOn}
                  {(order.userName ?? order.user?.fullname) ? ` · ${order.userName ?? order.user?.fullname}` : ''}
                </span>
              )}
            </JobMeta>
          </JobHeader>
        )}

        <Form form={form} layout="vertical" onFinish={handleUpdate} style={{ marginTop: 16 }}>
          <Form.Item name="id" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="startAt" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="endAt" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="userId" hidden>
            <Input size="small" />
          </Form.Item>
          {schedule && (
            <>
              <Form.Item
                required
                label={`Job date & time (${durationHours}h)`}
                extra="Leave the time as TBC if it is not decided yet"
              >
                <DateTimeSelect allowTbc dateTime={booking} setDate={setBookingDate} />
              </Form.Item>
              <Form.Item label="Technician" extra="Optional - can be assigned later in the day view">
                <TechnicianSelect
                  placeholder="Unassigned"
                  value={technicianId}
                  onSelect={(id) => {
                    setTechnicianId(id);
                    form.setFieldsValue({ userId: id });
                  }}
                  onClear={() => {
                    setTechnicianId(undefined);
                    form.setFieldsValue({ userId: undefined });
                  }}
                />
              </Form.Item>
            </>
          )}
          <Form.Item
            required
            name="dropOffDate"
            rules={Rules.required}
            label="Vehicle drop off"
          >
            <DateTimeSelect dateTime={dropOff} setDate={setDropOffDate} />
          </Form.Item>
          {dropOffOutOfSync && (
            <Alert
              type="error"
              showIcon
              style={{ fontSize: 12, marginBottom: 16 }}
              title="The drop-off date does not match the job date."
              action={
                <Button size="small" type="primary" onClick={handleSyncDropOff}>
                  Sync drop-off
                </Button>
              }
            />
          )}
          <NotifyRow>
            <div>
              <div style={{ fontWeight: 500 }}>Notify the customer</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Push notification to the mobile app with the drop-off time
              </Text>
            </div>
            <Form.Item name="sendPush" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </NotifyRow>
        </Form>
      </Modal>
    );
  }
);

const JobHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f7f8fa;
  border: 1px solid #eef0f3;
`;

const Plate = styled.span`
  font-weight: 700;
  font-size: 12px;
  background: rgba(242, 180, 7, 0.25);
  border-radius: 3px;
  padding: 1px 6px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const JobTitle = styled.span`
  font-weight: 600;
  font-size: 13px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const JobMeta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

const NotifyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #eef0f3;
`;
